import { z } from 'zod'

const identifier = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/)
const boundedText = (maximum: number) =>
  z
    .string()
    .max(maximum)
    .refine((value) =>
      [...value].every((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127),
    )

// A recovered URL restores the existing checkout. Its parameters never authorize payment.
const returnParamsSchema = z.object({
  purchase_flow: z.string().uuid().optional(),
  purchase_order_id: identifier,
  subscribe_id: identifier.optional(),
  product_id: identifier.optional(),
  coupon_id: boundedText(64).optional(),
  paymentToken: boundedText(4096).optional(),
  payment_token: boundedText(4096).optional(),
  txId: boundedText(256).optional(),
  tx_id: boundedText(256).optional(),
  billingKey: boundedText(2048).optional(),
  customer_uid: boundedText(256).optional(),
  issue_id: boundedText(256).optional(),
  imp_uid: boundedText(256).optional(),
  merchant_uid: boundedText(256).optional(),
  payment_id: boundedText(256).optional(),
  paymentId: boundedText(256).optional(),
  complete: z.literal('true').optional(),
  amount: z
    .string()
    .regex(/^\d{1,12}(?:\.0+)?$/)
    .optional(),
  pg_provider: z.enum(['KAKAO_PAY', 'TOSS_PAYMENTS', 'NAVER_PAY']).optional(),
  payment_type: z.enum(['FIRST_BILLING', 'TRIAL', 'LUMP_SUM', 'IPAD']).optional(),
  trial_level: z.enum(['B', 'C1', 'C2', 'D', 'SMART_TALK']).optional(),
  imp_success: z.enum(['true', 'false']).optional(),
  code: boundedText(128).optional(),
  error_msg: boundedText(1024).optional(),
})

export const krPaymentReturnTargetSchema = z.object({
  pathname: z
    .string()
    .regex(/^\/subscribes\/(?:payment\/[A-Za-z0-9_-]{1,128}|trial(?:\/smart-talk\/[A-Za-z0-9_-]{1,128})?)$/),
  searchParams: returnParamsSchema,
})

export type KrPaymentReturnTarget = z.infer<typeof krPaymentReturnTargetSchema>

export function parseKrPaymentReturnTarget(
  pathname: string,
  params: Record<string, unknown> | URLSearchParams,
): KrPaymentReturnTarget | null {
  if (
    params instanceof URLSearchParams &&
    Object.keys(returnParamsSchema.shape).some((key) => params.getAll(key).length > 1)
  ) {
    return null
  }
  const parsed = krPaymentReturnTargetSchema.safeParse({
    pathname,
    searchParams: params instanceof URLSearchParams ? Object.fromEntries(params) : params,
  })
  if (!parsed.success) {
    return null
  }
  const value = parsed.data
  for (const key of ['paymentId', 'payment_id', 'merchant_uid'] as const) {
    if (value.searchParams[key] && value.searchParams[key] !== value.searchParams.purchase_order_id) {
      return null
    }
  }
  for (const [first, second] of [
    ['paymentToken', 'payment_token'],
    ['txId', 'tx_id'],
    ['paymentId', 'payment_id'],
  ] as const) {
    if (
      value.searchParams[first] &&
      value.searchParams[second] &&
      value.searchParams[first] !== value.searchParams[second]
    ) {
      return null
    }
  }
  return JSON.stringify(value).length <= 12_000 ? value : null
}

export function krPaymentReturnPath(target: KrPaymentReturnTarget): string {
  const parsed = parseKrPaymentReturnTarget(target.pathname, target.searchParams)
  if (!parsed) {
    throw new Error('KR_PAYMENT_RETURN_INVALID')
  }
  return `${parsed.pathname}?${new URLSearchParams(parsed.searchParams)}`
}
